import { Editor, JSONContent } from '@tiptap/core';
import {
  ExportDocx,
  Docx,
  convertTextNode,
  pointsToHalfPoints,
  pointsToTwips,
} from '@tiptap-pro/extension-export-docx';
import { ExportPdf } from '@tiptap-pro/extension-export-pdf';
import { TIPTAP_CONVERTER_APP_ID } from 'config/environment';
import { readImageDimensions } from 'components/organisms/document-editor/extensions/image/utils';
import type {
  CustomNode,
  FooterConfig,
} from '@tiptap-pro/extension-export-pdf';

// ---------------------------------------------------------------------------
// Helpers — render Tiptap JSON content as DOCX elements (sync)
// ---------------------------------------------------------------------------

/** Strip control characters that are illegal in XML (and thus in DOCX Open XML).
 *  Keeps TAB (0x09), LF (0x0A), and CR (0x0D). Replaces others in 0x00-0x1F with empty string. */
function sanitizeText(text: string): string {
  // eslint-disable-next-line no-control-regex
  return text.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');
}

type DocxHeadingLevel =
  (typeof Docx.HeadingLevel)[keyof typeof Docx.HeadingLevel];

function docxHeadingLevelForTiptapLevel(level: number): DocxHeadingLevel {
  switch (level) {
    case 1:
      return Docx.HeadingLevel.HEADING_1;
    case 2:
      return Docx.HeadingLevel.HEADING_2;
    case 3:
      return Docx.HeadingLevel.HEADING_3;
    case 4:
      return Docx.HeadingLevel.HEADING_4;
    case 5:
      return Docx.HeadingLevel.HEADING_5;
    case 6:
      return Docx.HeadingLevel.HEADING_6;
    default:
      return Docx.HeadingLevel.HEADING_1;
  }
}

/** Convert inline content (text, hardBreak) to TextRun / ExternalHyperlink. RL_CITATION is skipped. */
function renderInlineContent(
  content: JSONContent[] | undefined,
): (
  | InstanceType<typeof Docx.TextRun>
  | InstanceType<typeof Docx.ExternalHyperlink>
)[] {
  if (!content) {
    return [];
  }

  return content.flatMap((child) => {
    if (child.type === 'text') {
      const sanitized = child.text
        ? { ...child, text: sanitizeText(child.text) }
        : child;

      return [
        convertTextNode(sanitized as Parameters<typeof convertTextNode>[0]),
      ];
    }
    if (child.type === 'RL_CITATION') {
      return [];
    }
    if (child.type === 'hardBreak') {
      return [new Docx.TextRun({ break: 1 })];
    }

    return [];
  });
}

type ExportImageEntry = {
  data: Uint8Array;
  width: number;
  height: number;
  type: 'jpg' | 'png';
};

let imageBytesBySrc: Record<string, ExportImageEntry> = {};
let displayedSizeBySrc: Record<string, { width: number; height: number }> = {};

const IMAGE_TYPE_BY_KEY: Record<string, ExportImageEntry['type']> = {
  jpeg: 'jpg',
  jpg: 'jpg',
  png: 'png',
};

function inferImageType(
  mime: string,
  src: string,
): ExportImageEntry['type'] | null {
  const mimeSubtype = mime.toLowerCase().split('/').pop() ?? '';
  const fromMime = IMAGE_TYPE_BY_KEY[mimeSubtype];

  if (fromMime) {
    return fromMime;
  }

  const ext = src.split('?')[0].split('.').pop()?.toLowerCase() ?? '';

  return IMAGE_TYPE_BY_KEY[ext] ?? null;
}

/** Fetch every image's bytes (given the presigned URLs already minted in
 *  `document.tsx`) and capture the size each image is currently displayed at
 *  in the editor DOM. Both are cached for the next export and must be awaited
 *  before `editor.commands.exportPdf(...)`. */
export async function prefetchImagesForExport(
  urls: string[],
  editor: Editor,
): Promise<void> {
  const displayed: Record<string, { width: number; height: number }> = {};

  editor.view.dom.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src');

    if (!src) {
      return;
    }
    const rect = img.getBoundingClientRect();
    const w = Math.round(rect.width);
    const h = Math.round(rect.height);

    if (w > 0 && h > 0) {
      displayed[src] = { width: w, height: h };
    }
  });
  displayedSizeBySrc = displayed;

  const next: Record<string, ExportImageEntry> = {};

  await Promise.all(
    Array.from(new Set(urls)).map(async (src) => {
      try {
        const res = await fetch(src, { mode: 'cors', cache: 'reload' });

        if (!res.ok) {
          return;
        }
        const blob = await res.blob();
        const type = inferImageType(blob.type, src);

        if (!type) {
          return;
        }
        const buf = new Uint8Array(await blob.arrayBuffer());
        const { width, height } = await readImageDimensions(blob);

        next[src] = { data: buf, width, height, type };
      } catch {
        // Skip unfetchable images — they'll simply be omitted from the export.
      }
    }),
  );

  imageBytesBySrc = next;
}

function renderImageNode(node: JSONContent): DocxBlock[] {
  const src = node.attrs?.src;

  if (typeof src !== 'string') {
    return [];
  }
  const entry = imageBytesBySrc[src];

  if (!entry) {
    return [];
  }

  // Priority: size the image is rendered at in the editor DOM →
  //           explicit width/height attrs on the node → natural file size.
  const displayed = displayedSizeBySrc[src];
  // const attrWidth = Number(node.attrs?.width);
  // const attrHeight = Number(node.attrs?.height);
  // const hasAttrW = Number.isFinite(attrWidth) && attrWidth > 0;
  // const hasAttrH = Number.isFinite(attrHeight) && attrHeight > 0;
  // const aspect = entry.height > 0 ? entry.width / entry.height : 1;

  let width: number = displayed.width;
  let height: number = displayed.height;

  // if (displayed) {
  // } else if (hasAttrW && hasAttrH) {
  //   width = attrWidth;
  //   height = attrHeight;
  // } else if (hasAttrW) {
  //   width = attrWidth;
  //   height = Math.round(attrWidth / aspect);
  // } else if (hasAttrH) {
  //   height = attrHeight;
  //   width = Math.round(attrHeight * aspect);
  // } else {
  //   width = entry.width;
  //   height = entry.height;
  // }

  // Clamp to the page's content area so images never bleed into the margins
  // or overflow a page (the renderer can't split an image across pages and
  // would otherwise push the whole thing to the next page, leaving a blank
  // gap). ImageRun expects pixels at 96 DPI; 1 inch = 1440 twips.
  // Page is 29.7cm tall with 2cm top/bottom margins; reserve a little extra
  // for the footer → ≈ 25cm of usable content height.
  const MAX_IMAGE_WIDTH_PX = Math.floor((CONTENT_WIDTH_TWIPS / 1440) * 96);
  const MAX_IMAGE_HEIGHT_PX = Math.floor((25 / 2.54) * 96);

  if (width > MAX_IMAGE_WIDTH_PX) {
    const scale = MAX_IMAGE_WIDTH_PX / width;

    width = MAX_IMAGE_WIDTH_PX;
    height = Math.round(height * scale);
  }
  if (height > MAX_IMAGE_HEIGHT_PX) {
    const scale = MAX_IMAGE_HEIGHT_PX / height;

    height = MAX_IMAGE_HEIGHT_PX;
    width = Math.round(width * scale);
  }

  // If the image takes up more than half of the page's usable content height,
  // start it on a fresh page. Otherwise it's likely to get pushed to the next
  // page anyway (since some prior content has eaten part of the current page),
  // leaving a large empty gap behind. A page break makes that intentional.
  const needsPageBreak = height > MAX_IMAGE_HEIGHT_PX / 2;

  return [
    new Docx.Paragraph({
      pageBreakBefore: needsPageBreak,
      children: [
        new Docx.ImageRun({
          type: entry.type,
          data: entry.data,
          transformation: { width, height },
        }),
      ],
    }),
  ];
}

function extractText(node: JSONContent): string {
  if (node.type === 'text') {
    return sanitizeText(node.text || '');
  }
  if (!node.content) {
    return '';
  }

  return node.content.map(extractText).join('');
}

type DocxBlock =
  | InstanceType<typeof Docx.Paragraph>
  | InstanceType<typeof Docx.Table>;

// ---------------------------------------------------------------------------
// Question-answer table helpers (QUESTION_ANSWER_ITEM / MULTI_QUESTION)
//
// Resolved CSS variables:
//   --divider-default  = --scarlet-gray-30 = #F4F4F4
//   --text-primary     = --gray-100        = #161616
//   --text-secondary   = --gray-70         = #525252
//   --font-size-base   = 0.875rem = 14px  ≈ 10.5pt
//   --font-weight-bold = 600
// ---------------------------------------------------------------------------

const NO_BORDER: Docx.IBorderOptions = {
  style: Docx.BorderStyle.NONE,
  size: 0,
  color: 'FFFFFF',
};

const NO_CELL_BORDERS = {
  top: NO_BORDER,
  bottom: NO_BORDER,
  left: NO_BORDER,
  right: NO_BORDER,
};

const NO_TABLE_BORDERS = {
  ...NO_CELL_BORDERS,
  insideHorizontal: NO_BORDER,
  insideVertical: NO_BORDER,
};

/** 2px solid divider — visible separator between QA rows in PDF */
const DIVIDER_BOTTOM_BORDERS = {
  top: NO_BORDER,
  left: NO_BORDER,
  right: NO_BORDER,
  bottom: {
    style: Docx.BorderStyle.SINGLE,
    size: 3,
    color: 'CCCCCC',
  } as Docx.IBorderOptions,
};

/** Recursively inject a bold mark into all text nodes. */
function injectBoldMarks(
  content: JSONContent[] | undefined,
): JSONContent[] | undefined {
  if (!content) {
    return content;
  }

  return content.map((node) => {
    if (node.type === 'text') {
      return { ...node, marks: [...(node.marks || []), { type: 'bold' }] };
    }
    if (node.content) {
      return { ...node, content: injectBoldMarks(node.content) };
    }

    return node;
  });
}

/** Top border on question row cells — creates separator between stacked QA items.
 *  Matches .tiptap table td { border-top: 1px solid #F4F4F4 } */
const QA_SEPARATOR_TOP_BORDERS = {
  top: {
    style: Docx.BorderStyle.SINGLE,
    size: 1,
    color: 'CCCCCC',
  } as Docx.IBorderOptions,
  bottom: NO_BORDER,
  left: NO_BORDER,
  right: NO_BORDER,
};

type QATableStyle = {
  firstColWidthPct: number;
  /** QUESTION_ANSWER_ITEM in MULTI_QUESTIONS group has no even-row dividers; standalone MULTI_QUESTION does. */
  showRowDividers: boolean;
};

const QA_ITEM_STYLE: QATableStyle = {
  firstColWidthPct: 8,
  showRowDividers: false,
};
const MULTI_Q_STYLE: QATableStyle = {
  firstColWidthPct: 5,
  showRowDividers: true,
};

/** Render the content of a QUESTION_ANSWER_ITEM or MULTI_QUESTION section. */
function renderQuestionAnswerContent(
  content: JSONContent[] | undefined,
  style: QATableStyle,
): DocxBlock[] {
  if (!content) {
    return [];
  }

  return content.flatMap((child) => {
    if (child.type === 'table') {
      return [renderQuestionAnswerTable(child, style)];
    }

    // Non-table content (headings, etc.) — render normally
    return renderBlockContent([child]);
  });
}

// A4 (21 cm) minus 2.5 cm left + 2.5 cm right margins = 16 cm content width
// 1 cm ≈ 567 twips → 16 cm ≈ 9072 twips
const CONTENT_WIDTH_TWIPS = 9072;

/**
 * Render a QA table matching the editor styling.
 *
 * QUESTION_ANSWER_ITEM (inside MULTI_QUESTIONS group):
 *   table border: 0, td border-bottom: none, tr border-left: none
 *   tr td:first-child width 8%, centered
 *   tr:nth-child(odd) — bold, --text-primary, --font-size-base
 *   NO divider on even rows
 *
 * MULTI_QUESTION (standalone):
 *   Same as above but first-child width 5%
 *   tr:nth-child(even) — border-bottom: 2px solid --divider-default
 *   RL_PLACEHOLDER_CONFIG — color: --text-secondary, font-weight: regular
 */
function renderQuestionAnswerTable(
  tableNode: JSONContent,
  style: QATableStyle,
): InstanceType<typeof Docx.Table> {
  const firstColTwips = Math.round(
    CONTENT_WIDTH_TWIPS * (style.firstColWidthPct / 100),
  );
  const secondColTwips = CONTENT_WIDTH_TWIPS - firstColTwips;

  const rows = (tableNode.content || []).map((row, rowIdx) => {
    // CSS nth-child is 1-indexed: odd (1st,3rd) = question, even (2nd,4th) = answer
    const isQuestionRow = rowIdx % 2 === 0;

    const cells = (row.content || []).map((cell, colIdx) => {
      const isFirstCol = colIdx === 0;
      // Editor: .tiptap table td { border-top: 1px solid #F4F4F4 }
      //         .MULTI_QUESTIONS td { border-bottom: none }
      // → every row gets a top border; only MULTI_QUESTION adds bottom on even rows
      const cellBorders =
        style.showRowDividers && !isQuestionRow
          ? DIVIDER_BOTTOM_BORDERS
          : QA_SEPARATOR_TOP_BORDERS;

      if (isFirstCol) {
        // First column: narrow, centered number (e.g. "1.") or empty
        const text = extractText({ content: cell.content });

        return new Docx.TableCell({
          children: [
            new Docx.Paragraph({
              children: text
                ? [
                    new Docx.TextRun({
                      text,
                      bold: isQuestionRow,
                      font: 'sans-serif',
                      color: isQuestionRow ? '161616' : undefined,
                      size: pointsToHalfPoints(6), // 8px — matches body text
                    }),
                  ]
                : [],
              alignment: Docx.AlignmentType.CENTER,
              spacing: { after: 0 },
            }),
          ],
          width: { size: firstColTwips, type: Docx.WidthType.DXA },
          borders: cellBorders,
          verticalAlign: Docx.VerticalAlignTable.TOP,
          margins: {
            top: pointsToTwips(2),
            bottom: pointsToTwips(2),
            left: pointsToTwips(2),
            right: pointsToTwips(2),
          },
        });
      }

      // Second column: question text (bold) or answer content
      if (isQuestionRow) {
        const processedContent = injectBoldMarks(cell.content);
        const cellContent = renderBlockContent(processedContent);
        const children =
          cellContent.length > 0
            ? cellContent
            : [new Docx.Paragraph({ children: [] })];

        return new Docx.TableCell({
          children,
          width: { size: secondColTwips, type: Docx.WidthType.DXA },
          borders: cellBorders,
          margins: {
            top: pointsToTwips(2),
            bottom: pointsToTwips(2),
            left: pointsToTwips(4),
            right: pointsToTwips(4),
          },
        });
      }

      // Answer row — render content; RL_PLACEHOLDER_CONFIG uses secondary color
      const cellContent = renderBlockContent(cell.content);
      const children =
        cellContent.length > 0
          ? cellContent
          : [new Docx.Paragraph({ children: [] })];

      return new Docx.TableCell({
        children,
        width: { size: secondColTwips, type: Docx.WidthType.DXA },
        borders: cellBorders,
        margins: {
          top: pointsToTwips(2),
          bottom: pointsToTwips(2),
          left: pointsToTwips(4),
          right: pointsToTwips(4),
        },
      });
    });

    return new Docx.TableRow({ children: cells });
  });

  return new Docx.Table({
    rows,
    width: { size: CONTENT_WIDTH_TWIPS, type: Docx.WidthType.DXA },
    columnWidths: [firstColTwips, secondColTwips],
    borders: NO_TABLE_BORDERS,
  });
}

/** Convert block-level Tiptap nodes to an array of Docx Paragraphs / Tables. */
export function renderBlockContent(
  content: JSONContent[] | undefined,
): DocxBlock[] {
  if (!content) {
    return [];
  }

  return content.flatMap((child): DocxBlock[] => {
    switch (child.type) {
      case 'heading': {
        const level = child.attrs?.level ?? 1;

        return [
          new Docx.Paragraph({
            children: renderInlineContent(child.content),
            heading: docxHeadingLevelForTiptapLevel(level),
          }),
        ];
      }

      case 'paragraph':
        return [
          new Docx.Paragraph({
            children: renderInlineContent(child.content),
          }),
        ];

      case 'bulletList':
        return renderListItems(child.content, 'bullet');

      case 'orderedList':
        return renderListItems(child.content, 'ordered');

      case 'taskList':
        return renderTaskListItems(child.content);

      case 'table':
        return [renderTable(child)];

      case 'blockquote':
        return (child.content || []).flatMap((quoteChild) =>
          renderBlockContent([quoteChild]).map(
            (_p) =>
              new Docx.Paragraph({
                children: renderInlineContent(quoteChild.content),
                style: 'Quote',
                indent: { left: pointsToTwips(18) },
              }),
          ),
        );

      case 'codeBlock':
        // Editor: pre { background: #404040, border: 1px #000, padding: 16px,
        //   color: white, font: monospace, margin: 48px top/bottom }
        return [
          new Docx.Paragraph({
            children: [
              new Docx.TextRun({
                text: extractText(child),
                font: 'Courier New',
                size: pointsToHalfPoints(6),
                color: 'FFFFFF',
              }),
            ],
            shading: {
              fill: '404040',
              type: Docx.ShadingType.CLEAR,
              color: 'auto',
            },
            border: {
              top: { style: Docx.BorderStyle.SINGLE, size: 1, color: '000000' },
              bottom: {
                style: Docx.BorderStyle.SINGLE,
                size: 1,
                color: '000000',
              },
              left: {
                style: Docx.BorderStyle.SINGLE,
                size: 1,
                color: '000000',
              },
              right: {
                style: Docx.BorderStyle.SINGLE,
                size: 1,
                color: '000000',
              },
            },
            spacing: { before: pointsToTwips(12), after: pointsToTwips(12) },
            indent: { left: pointsToTwips(6), right: pointsToTwips(6) },
          }),
        ];

      case 'image':
        return renderImageNode(child);

      case 'horizontalRule':
        // Editor: hr { border: 1px solid rgba(0,0,0,0.2), margin: 32px }
        return [
          new Docx.Paragraph({
            children: [],
            border: {
              bottom: {
                style: Docx.BorderStyle.SINGLE,
                size: 1,
                color: 'CCCCCC', // rgba(0,0,0,0.2) on white
              },
            },
            spacing: { before: pointsToTwips(24), after: pointsToTwips(24) },
          }),
        ];

      // Nested container nodes — recurse into children
      case 'RL_CUSTOM_SECTION': {
        const sectionType = child.attrs?.sectionType;

        if (sectionType === 'QUESTION_ANSWER_ITEM') {
          return renderQuestionAnswerContent(child.content, QA_ITEM_STYLE);
        }
        if (sectionType === 'MULTI_QUESTION') {
          return renderQuestionAnswerContent(child.content, MULTI_Q_STYLE);
        }

        return renderBlockContent(child.content);
      }
      case 'RL_GROUP_SECTION':
      case 'RL_GROUPED_SECTION_TEMP':
      case 'tableHeaderLeafNode':
        return renderBlockContent(child.content);

      case 'RL_PLACEHOLDER_CONFIG': {
        const phText = extractText(child);

        return [
          new Docx.Paragraph({
            children: [
              new Docx.TextRun({
                text: phText || '[ ]',
                color: '525252', // --text-secondary
                font: 'sans-serif',
                size: pointsToHalfPoints(6),
              }),
            ],
            spacing: { after: 0 },
          }),
        ];
      }

      default: {
        // Unknown block — extract text as a fallback paragraph
        const text = extractText(child);

        if (text) {
          return [
            new Docx.Paragraph({
              children: [new Docx.TextRun({ text })],
            }),
          ];
        }

        return [];
      }
    }
  });
}

/** Convert a Tiptap table node to a Docx.Table matching editor table styles.
 *
 * Editor CSS (.tiptap table):
 *   td, th: border top/bottom/right 1px solid #F4F4F4, padding: 2px 8px
 *   th: font-weight 500 (semi-bold), font-size --font-size-base (14px)
 *   td: background white, font-size --font-size-base (14px)
 */
function renderTable(node: JSONContent): InstanceType<typeof Docx.Table> {
  const rows = (node.content || []).map((row, rowIdx) => {
    // Filter out ghost columns (the "+ Add a column" placeholder)
    const nonGhostCells = (row.content || []).filter(
      (cell) => !cell.attrs?.ghost,
    );

    const isHeaderRow = nonGhostCells.some(
      (cell) => cell.type === 'tableHeader',
    );

    const cells = nonGhostCells.map((cell) => {
      // For header cells, inject bold marks into content
      const content =
        isHeaderRow && cell.type === 'tableHeader'
          ? injectBoldMarks(cell.content)
          : cell.content;

      const cellContent = renderBlockContent(content);

      // Ensure at least one paragraph — DOCX cells must not be empty
      const children =
        cellContent.length > 0
          ? cellContent
          : [new Docx.Paragraph({ children: [] })];

      return new Docx.TableCell({
        children,
        columnSpan: cell.attrs?.colspan ?? 1,
        rowSpan: cell.attrs?.rowspan ?? 1,
        // Editor: padding: var(--spacing-01) var(--spacing-03) → 2px 8px → 1.5pt 6pt
        margins: {
          top: pointsToTwips(1.5),
          bottom: pointsToTwips(1.5),
          left: pointsToTwips(6),
          right: pointsToTwips(6),
        },
        verticalAlign: Docx.VerticalAlignTable.TOP,
      });
    });

    return new Docx.TableRow({
      children: cells,
      // Mark first row as table header for repeat on page breaks
      tableHeader: rowIdx === 0,
    });
  });

  return new Docx.Table({
    rows,
    width: { size: 100, type: Docx.WidthType.PERCENTAGE },
    borders: TABLE_BORDERS,
  });
}

function renderListItems(
  content: JSONContent[] | undefined,
  type: 'bullet' | 'ordered',
): DocxBlock[] {
  if (!content) {
    return [];
  }

  return content.flatMap((item) =>
    (item.content || []).flatMap((child) => {
      if (child.type === 'paragraph') {
        return [
          new Docx.Paragraph({
            children: renderInlineContent(child.content),
            ...(type === 'bullet'
              ? { bullet: { level: 0 } }
              : { numbering: { reference: 'default-numbering', level: 0 } }),
          }),
        ];
      }
      if (child.type === 'bulletList') {
        return renderListItems(child.content, 'bullet');
      }
      if (child.type === 'orderedList') {
        return renderListItems(child.content, 'ordered');
      }

      return renderBlockContent([child]);
    }),
  );
}

function renderTaskListItems(content: JSONContent[] | undefined): DocxBlock[] {
  if (!content) {
    return [];
  }

  return content.flatMap((taskItem) => {
    if (taskItem.type !== 'taskItem') {
      return renderBlockContent([taskItem]);
    }

    const checked = taskItem.attrs?.checked === true;
    const prefix = checked ? '\u2611 ' : '\u2610 ';

    return (taskItem.content || []).flatMap((child) => {
      if (child.type === 'paragraph') {
        // For checked items, inject a strike mark into text nodes so
        // convertTextNode renders them with strikethrough
        const modifiedContent =
          checked && child.content
            ? child.content.map((c) =>
                c.type === 'text'
                  ? {
                      ...c,
                      marks: [...(c.marks || []), { type: 'strike' as const }],
                    }
                  : c,
              )
            : child.content;

        // Editor: ul[data-type='taskList'] { list-style-type: none; padding: 0 }
        // → no bullet marker, no indent, just checkbox prefix + text
        return [
          new Docx.Paragraph({
            children: [
              new Docx.TextRun({ text: prefix }),
              ...renderInlineContent(modifiedContent),
            ],
          }),
        ];
      }

      return renderBlockContent([child]);
    });
  });
}

// ---------------------------------------------------------------------------
// Custom node renderers — all custom nodes rendered via Docx elements
// ---------------------------------------------------------------------------

export const pdfExportCustomNodes: CustomNode[] = [
  // RL_CITATION — skip in export
  {
    type: 'RL_CITATION',
    render: () => null,
  },

  // Image — override the extension's built-in image renderer so every image
  // (top-level and nested) goes through our bytes-based path with width
  // clamping and page-break heuristics. The built-in renderer ships the src
  // URL to the converter and can produce oversized layout for tall images.
  {
    type: 'image',
    render: (node) => renderImageNode(node) as any,
  },

  // Container custom nodes — render their nested content
  {
    type: 'RL_CUSTOM_SECTION',
    render: (node) => {
      const sectionType = node.attrs?.sectionType;

      if (sectionType === 'QUESTION_ANSWER_ITEM') {
        return renderQuestionAnswerContent(node.content, QA_ITEM_STYLE) as any;
      }
      if (sectionType === 'MULTI_QUESTION') {
        return renderQuestionAnswerContent(node.content, MULTI_Q_STYLE) as any;
      }

      return renderBlockContent(node.content) as any;
    },
  },
  {
    type: 'RL_GROUP_SECTION',
    render: (node) => renderBlockContent(node.content) as any,
  },
  {
    type: 'RL_GROUPED_SECTION_TEMP',
    render: (node) => renderBlockContent(node.content) as any,
  },
  {
    type: 'tableHeaderLeafNode',
    render: (node) => renderBlockContent(node.content) as any,
  },

  // Placeholder config — secondary color, regular weight (matches editor CSS)
  {
    type: 'RL_PLACEHOLDER_CONFIG',
    render: (node) => {
      const text = extractText(node);

      return new Docx.Paragraph({
        children: [
          new Docx.TextRun({
            text: text || '[ ]',
            color: '525252', // --text-secondary
            font: 'sans-serif',
            size: pointsToHalfPoints(6), // --font-size-base
          }),
        ],
        spacing: { after: 0 },
      });
    },
  },

  // Task list — render as bullet paragraphs with checkbox prefix
  {
    type: 'taskList',
    render: (node) => renderTaskListItems(node.content) as any,
  },
];

// ---------------------------------------------------------------------------
// Style overrides — PDF export sizes
// https://tiptap.dev/docs/conversion/export/pdf/export-styles
//
// PDF export values (1px ≈ 0.75pt):
//   Body text: 8px = 6pt, line-height 1.6
//   h1: 16px = 12pt     h2: 12px = 9pt      h3: 10px = 7.5pt
//   h4: 8px  = 6pt      h5: 6px  = 4.5pt    h6: 4px  = 3pt
//   All headings & paragraphs: line-height 1.6
//   Font family: Arial
// ---------------------------------------------------------------------------
const pdfExportStyleOverrides = {
  paragraphStyles: [
    {
      id: 'Normal',
      name: 'Normal',
      run: { font: 'sans-serif', color: '161616' },
    },
    {
      id: 'Heading1',
      name: 'Heading 1',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: {
        font: 'sans-serif',
        color: '161616',
      },
    },
    {
      id: 'Heading2',
      name: 'Heading 2',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: {
        font: 'sans-serif',
        color: '161616',
      },
    },
    {
      id: 'Heading3',
      name: 'Heading 3',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: {
        font: 'sans-serif',
        color: '161616',
      },
    },
    {
      id: 'Heading4',
      name: 'Heading 4',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: {
        font: 'sans-serif',
        color: '161616',
      },
    },
    {
      id: 'Heading5',
      name: 'Heading 5',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: {
        font: 'sans-serif',
        color: '161616',
      },
    },
    {
      id: 'Heading6',
      name: 'Heading 6',
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: {
        font: 'sans-serif',
        color: '161616',
      },
    },
    {
      id: 'ListParagraph',
      name: 'List Paragraph',
      basedOn: 'Normal',
      run: { font: 'sans-serif' }, //size: pointsToHalfPoints(6) 8px same as body
    },
  ],
};

// Table border — darker color for PDF visibility
const TABLE_BORDER: Docx.IBorderOptions = {
  style: Docx.BorderStyle.SINGLE,
  size: 1,
  color: 'CCCCCC',
};

const TABLE_BORDERS = {
  top: TABLE_BORDER,
  bottom: TABLE_BORDER,
  left: TABLE_BORDER,
  right: TABLE_BORDER,
  insideHorizontal: TABLE_BORDER,
  insideVertical: TABLE_BORDER,
};

// ---------------------------------------------------------------------------
// Extension factories
// ---------------------------------------------------------------------------

export function createExportDocxExtension() {
  return ExportDocx.configure({
    onCompleteExport: () => {},
    // Table styling that matches the editor's CSS
    // ExportPdf does not forward these, but ExportDocx uses them as fallback
    tableOverrides: {
      borders: TABLE_BORDERS,
      width: { size: 100, type: Docx.WidthType.PERCENTAGE },
    },
    tableCellOverrides: {
      // Matches editor: padding: var(--spacing-01) var(--spacing-03) → 2px 8px → 1.5pt 6pt
      margins: {
        top: pointsToTwips(1.5),
        bottom: pointsToTwips(1.5),
        left: pointsToTwips(6),
        right: pointsToTwips(6),
      },
    },
    textRunOverrides: {
      font: 'sans-serif',
    },
  });
}

export function createExportPdfExtension(
  onCompleteExport: (blob: Blob) => void,
) {
  // Add page numbers to the footer
  const pdfFooters = {
    default: async () =>
      new Docx.Footer({
        children: [
          new Docx.Paragraph({
            tabStops: [
              {
                type: Docx.TabStopType.RIGHT,
                position: Docx.TabStopPosition.MAX,
              },
            ],
            children: [
              new Docx.TextRun({
                text: `Generated at: ${new Date().toLocaleString()}`,
                font: 'sans-serif',
              }),
              new Docx.TextRun({
                font: 'sans-serif',
                children: [
                  new Docx.Tab(),
                  Docx.PageNumber.CURRENT,
                  ' of ',
                  Docx.PageNumber.TOTAL_PAGES,
                ],
              }),
            ],
          }),
        ],
      }),
  };

  return ExportPdf.configure({
    appId: TIPTAP_CONVERTER_APP_ID,
    customNodes: pdfExportCustomNodes,
    styleOverrides: pdfExportStyleOverrides,
    pageSize: {
      width: '21cm' as const,
      height: '29.7cm' as const,
    },
    pageMargins: {
      top: '2cm' as const,
      bottom: '2cm' as const,
      left: '2.5cm' as const,
      right: '2.5cm' as const,
    },
    footers: pdfFooters as FooterConfig,
    onCompleteExport,
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function downloadPdfBlob(blob: Blob, filename = 'document.pdf') {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
